import { useState, useEffect, useRef } from "react";

export interface PremiumPopupStat {
  value: string;
  label: string;
  color: string;
}

export interface PremiumPopupAdvantage {
  icon: string;
  color: string;
  title: string;
  description: string;
}

export interface PremiumPopupStep {
  icon: string;
  label: string;
}

export interface PremiumPopupConfig {
  label: string;
  icon: string;
  heroImageUrl: string;
  badgeText: string;
  title: string;
  subtitle: string;
  description: string;
  stats: PremiumPopupStat[];
  advantages: PremiumPopupAdvantage[];
  steps: PremiumPopupStep[];
  ctaText: string;
}

// ── Config presets for each locked feature ────────────────────────────────────

export const PREMIUM_POPUP_CONFIGS: Record<string, PremiumPopupConfig> = {
  livesupport: {
    label: "Destek Merkezi",
    icon: "ri-customer-service-2-line",
    badgeText: "Premium Özellik",
    title: "Canlı Destek Merkezi",
    subtitle: "Ziyaretçilerle anlık iletişim kurun",
    description: "Sitenize entegre canlı sohbet widgetı ile ziyaretçilerinize anında yardım sunun. Otomatik zamanlama, agent yönetimi ve mesaj geçmişiyle eksiksiz bir destek deneyimi.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=modern%20dark%20customer%20support%20live%20chat%20interface%20with%20glowing%20green%20indicators%20agent%20dashboard%20professional%20support%20center%20neon%20accents%20dark%20background%20digital%20art%20cinematic%20high%20quality&width=900&height=360&seq=ls-hero-001&orientation=landscape",
    stats: [
      { value: "7/24", label: "Aktif Destek", color: "#10B981" },
      { value: "Canlı", label: "Chat Widget", color: "#60A5FA" },
      { value: "Otomatik", label: "Zamanlama", color: "#F59E0B" },
    ],
    advantages: [
      { icon: "ri-chat-smile-2-line",      color: "#10B981", title: "Anlık Canlı Chat",         description: "Ziyaretçiler sitede anında sizinle sohbet başlatabilir" },
      { icon: "ri-time-line",              color: "#60A5FA", title: "Otomatik Zamanlama",       description: "Mesai saatlerini ayarlayın, sistem online/offline geçişi yapar" },
      { icon: "ri-user-settings-line",     color: "#F59E0B", title: "Agent Profili",            description: "Agent adı, fotoğrafı ve unvanını kontrol panelinden düzenle" },
      { icon: "ri-history-line",           color: "#A78BFA", title: "Mesaj Geçmişi",            description: "Tüm sohbet geçmişi Destek Merkezi'nde kayıt altında" },
      { icon: "ri-mail-send-line",         color: "#F43F5E", title: "Offline Form",             description: "Destek kapalıyken ziyaretçi form bırakabilir, bildirim alırsınız" },
      { icon: "ri-notification-3-line",    color: "#34D399", title: "Okunmamış Bildirim",       description: "Yeni mesaj sayacı gerçek zamanlı sidebar'da güncellenir" },
    ],
    steps: [
      { icon: "ri-settings-3-line",   label: "Ayarla" },
      { icon: "ri-user-line",         label: "Agent Kur" },
      { icon: "ri-toggle-line",       label: "Aktif Et" },
      { icon: "ri-chat-smile-2-line", label: "Destekle" },
    ],
    ctaText: "Aktifleştirmek için geliştiriciniz ile iletişime geçin",
  },

  changelog: {
    label: "Değişiklik Günlüğü",
    icon: "ri-history-line",
    badgeText: "Premium Özellik",
    title: "Değişiklik Günlüğü",
    subtitle: "Tüm site değişikliklerini takip edin",
    description: "Kontrol panelinde yapılan her düzenleme otomatik kaydedilir. Kim, ne zaman, hangi alanda ne değiştirdi — hepsini geri alabilir, raporlayabilirsiniz.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20audit%20log%20dashboard%20with%20timeline%20entries%20glowing%20blue%20timestamps%20user%20activity%20tracking%20interface%20professional%20dark%20background%20digital%20art%20high%20quality&width=900&height=360&seq=cl-hero-001&orientation=landscape",
    stats: [
      { value: "200", label: "Maks. Kayıt", color: "#10B981" },
      { value: "Kim?", label: "Kullanıcı Kaydı", color: "#60A5FA" },
      { value: "Geri Al", label: "Destekler", color: "#F59E0B" },
    ],
    advantages: [
      { icon: "ri-user-line",              color: "#10B981", title: "Kullanıcı Bazlı Log",      description: "Her değişiklikte hangi admin/editörün yaptığı kaydedilir" },
      { icon: "ri-calendar-2-line",        color: "#60A5FA", title: "Tarih & Saat Damgası",     description: "Her kayıtta tam tarih ve saat bilgisi, kolay filtreleme" },
      { icon: "ri-layout-masonry-line",    color: "#F59E0B", title: "Bölüm Bazlı Takip",        description: "Hero, Projeler, Ortaklar — hangi bölüm değiştirildi görürsünüz" },
      { icon: "ri-arrow-go-back-line",     color: "#A78BFA", title: "Geri Alma Desteği",        description: "Eski değerlere bakarak istediğiniz noktaya dönebilirsiniz" },
      { icon: "ri-eye-line",               color: "#F43F5E", title: "Eski & Yeni Karşılaştırma", description: "Değişiklik öncesi ve sonrası değerleri yan yana görün" },
      { icon: "ri-file-list-3-line",       color: "#34D399", title: "200 Kayıt Kapasitesi",     description: "Son 200 değişiklik saklanır, sıralı ve aranabilir liste" },
    ],
    steps: [
      { icon: "ri-pencil-fill",        label: "Düzenleme Yap" },
      { icon: "ri-save-line",          label: "Otomatik Kayıt" },
      { icon: "ri-history-line",       label: "Geçmişe Bak" },
      { icon: "ri-arrow-go-back-line", label: "Geri Al" },
    ],
    ctaText: "Aktifleştirmek için geliştiriciniz ile iletişime geçin",
  },

  ad_manager: {
    label: "Reklam Yönetimi",
    icon: "ri-advertisement-line",
    badgeText: "Premium Özellik",
    title: "Reklam Yönetimi",
    subtitle: "Siteye özel reklam popup sistemi",
    description: "Ziyaretçilerinize ilk girişte tam ekran reklam gösterin. Görsel yükle, link ekle, aktifleştir — hiç kod yazmadan profesyonel interstitial reklam sistemi.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20advertisement%20management%20dashboard%20popup%20overlay%20interface%20glowing%20amber%20orange%20indicators%20professional%20digital%20marketing%20dashboard%20dark%20background%20cinematic%20high%20quality&width=900&height=360&seq=ad-hero-001&orientation=landscape",
    stats: [
      { value: "Tam Ekran", label: "Popup Reklam", color: "#10B981" },
      { value: "Özel", label: "Link Desteği", color: "#60A5FA" },
      { value: "Süre", label: "Ayarlanabilir", color: "#F59E0B" },
    ],
    advantages: [
      { icon: "ri-image-add-line",         color: "#10B981", title: "Görsel Yükleme",           description: "PNG, JPG, GIF, WebP formatlarını direkt yükleyin" },
      { icon: "ri-links-line",             color: "#60A5FA", title: "Tıklanabilir Link",         description: "Reklama tıklayan ziyaretçi istediğiniz sayfaya yönlendirilir" },
      { icon: "ri-toggle-line",            color: "#F59E0B", title: "Tek Tıkla Aktifleştir",    description: "Hangi reklamın gösterileceğini tek tıkla seçin" },
      { icon: "ri-timer-line",             color: "#A78BFA", title: "Yeniden Gösterim Süresi",  description: "Aynı ziyaretçiye ne sıklıkta gösterileceğini saat olarak ayarlayın" },
      { icon: "ri-zoom-in-line",           color: "#F43F5E", title: "Önizleme Modu",            description: "Reklamı yayınlamadan önce tam önizlemesiyle kontrol edin" },
      { icon: "ri-image-2-line",           color: "#34D399", title: "Çoklu Reklam Havuzu",      description: "Birden fazla reklam yükleyin, dilediğinizi aktif edin" },
    ],
    steps: [
      { icon: "ri-upload-cloud-2-line", label: "Görsel Yükle" },
      { icon: "ri-links-line",          label: "Link Ekle" },
      { icon: "ri-toggle-line",         label: "Aktif Et" },
      { icon: "ri-eye-line",            label: "Yayında!" },
    ],
    ctaText: "Aktifleştirmek için geliştiriciniz ile iletişime geçin",
  },

  color_palette: {
    label: "Renk Paleti",
    icon: "ri-palette-line",
    badgeText: "Premium Özellik",
    title: "Site Renk Paleti",
    subtitle: "Tüm platformu tek tıkla yeniden tasarlayın",
    description: "Kontrol paneli ve sitenin tüm renklerini merkezi bir yerden yönetin. Hazır tema paketleri veya tamamen özel renk kombinasyonlarıyla markanıza özgü bir görünüm oluşturun.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20color%20palette%20design%20interface%20vibrant%20swatches%20gradient%20sliders%20premium%20UI%20design%20tool%20glowing%20colors%20dark%20background%20cinematic%20professional%20digital%20art%20high%20quality&width=900&height=360&seq=cp-hero-001&orientation=landscape",
    stats: [
      { value: "5+", label: "Hazır Tema", color: "#EC4899" },
      { value: "8", label: "Renk Değişkeni", color: "#60A5FA" },
      { value: "Anlık", label: "Önizleme", color: "#F59E0B" },
    ],
    advantages: [
      { icon: "ri-palette-fill",           color: "#EC4899", title: "Hazır Tema Paketleri",    description: "Gece Mavisi, Toprak Tonu, Orman Yeşili ve daha fazla profesyonel tema" },
      { icon: "ri-brush-line",             color: "#60A5FA", title: "Manuel Renk Düzenle",     description: "Ana renk, vurgu, arka plan, kart ve kenarlık renklerini tek tek ayarla" },
      { icon: "ri-eye-line",               color: "#F59E0B", title: "Anlık Canlı Önizleme",    description: "Değişiklikler sayfayı yenilemeden anında hem panel hem sitede görünür" },
      { icon: "ri-layout-grid-line",       color: "#10B981", title: "Tam Kapsam",              description: "Panel, header, sidebar, kartlar — 8 farklı renk değişkeni tek yerden" },
      { icon: "ri-save-line",              color: "#A78BFA", title: "Kalıcı Kayıt",             description: "Seçilen palette tarayıcıya kaydedilir, sayfa yenilese de korunur" },
      { icon: "ri-refresh-line",           color: "#34D399", title: "Varsayılana Dön",          description: "Tek tıkla orijinal Gece Mavisi temaya sıfırlama" },
    ],
    steps: [
      { icon: "ri-palette-line",  label: "Tema Seç" },
      { icon: "ri-brush-line",    label: "Özelleştir" },
      { icon: "ri-eye-line",      label: "Önizle" },
      { icon: "ri-save-line",     label: "Uygula" },
    ],
    ctaText: "Aktifleştirmek için geliştiriciniz ile iletişime geçin",
  },

  undo_enabled: {
    label: "Sınırsız Geri Al",
    icon: "ri-arrow-go-back-line",
    badgeText: "Premium Özellik",
    title: "Sınırsız Geri Al",
    subtitle: "Her değişikliği güvenle yapın",
    description: "Kontrol panelinde yaptığınız tüm düzenlemeleri geri alabilirsiniz. Kilitli modda günde yalnızca 3 geri alma hakkınız var — Premium ile bu limit tamamen kalkar.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20undo%20history%20panel%20interface%20timeline%20arrows%20glowing%20blue%20indicators%20edit%20history%20version%20control%20professional%20dashboard%20dark%20background%20cinematic&width=900&height=360&seq=undo-hero-001&orientation=landscape",
    stats: [
      { value: "Sınırsız", label: "Geri Alma", color: "#10B981" },
      { value: "3/gün", label: "Kilitli Limit", color: "#F43F5E" },
      { value: "Tüm", label: "Editörlerde", color: "#60A5FA" },
    ],
    advantages: [
      { icon: "ri-arrow-go-back-line",     color: "#10B981", title: "Sınırsız Geri Al",         description: "Günlük limit yok — istediğiniz kadar geri alabilirsiniz" },
      { icon: "ri-arrow-go-forward-line",  color: "#60A5FA", title: "Yeniden Uygula",           description: "Geri aldığınız değişikliği tekrar uygulayabilirsiniz" },
      { icon: "ri-history-line",           color: "#F59E0B", title: "Tüm Editörlerde",          description: "Hero, Projeler, Ortaklar, Hakkımızda — her bölümde çalışır" },
      { icon: "ri-shield-check-line",      color: "#A78BFA", title: "Güvenli Düzenleme",        description: "Yanlışlıkla silme veya değiştirme korkusu olmadan çalışın" },
      { icon: "ri-focus-3-line",           color: "#F43F5E", title: "Anlık Geri Yükleme",      description: "Değişiklik anında geri alınır, sayfa yenilemek gerekmez" },
      { icon: "ri-bar-chart-line",         color: "#34D399", title: "Kilitli Mod Bilgisi",     description: "Kapalıyken 24 saatte 3 geri alma hakkı — ihtiyatlı kullanım" },
    ],
    steps: [
      { icon: "ri-pencil-fill",           label: "Değiştir" },
      { icon: "ri-close-circle-line",     label: "Hata Fark Et" },
      { icon: "ri-arrow-go-back-line",    label: "Geri Al" },
      { icon: "ri-check-double-line",     label: "Kurtarıldı!" },
    ],
    ctaText: "Aktifleştirmek için geliştiriciniz ile iletişime geçin",
  },

  drag_drop_projects: {
    label: "Projelerde Sürükle-Bırak",
    icon: "ri-drag-drop-line",
    badgeText: "Premium Özellik",
    title: "Sürükle-Bırak Sıralama",
    subtitle: "Projeleri istediğiniz sıraya dizin",
    description: "Proje listesini elle sürükleyerek sıralayın. Kapalıyken projeler otomatik tarih sıralamasına geçer — Premium ile tam kontrol sizde.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20drag%20and%20drop%20list%20reordering%20interface%20glowing%20handles%20project%20management%20dashboard%20cards%20professional%20dark%20background%20cinematic&width=900&height=360&seq=dd-hero-001&orientation=landscape",
    stats: [
      { value: "Elle", label: "Sıralama", color: "#10B981" },
      { value: "Anlık", label: "Kayıt", color: "#60A5FA" },
      { value: "Tüm", label: "Projeler", color: "#F59E0B" },
    ],
    advantages: [
      { icon: "ri-drag-drop-line",          color: "#10B981", title: "Sürükle-Bırak Sıralama",   description: "Projeleri fare ile tutup istediğiniz yere taşıyın" },
      { icon: "ri-save-line",               color: "#60A5FA", title: "Otomatik Kayıt",           description: "Sıralama değişikliği anında sunucuya kaydedilir" },
      { icon: "ri-sort-desc",               color: "#F59E0B", title: "Kilitli Mod",              description: "Kapalıyken sistem tarihe göre otomatik sıralar" },
      { icon: "ri-layout-grid-line",        color: "#A78BFA", title: "Tüm Projelerde",           description: "Hem liste hem kart görünümünde sıralama çalışır" },
      { icon: "ri-eye-line",                color: "#F43F5E", title: "Sitede Anında Görünür",    description: "Panel sıralaması direkt siteye yansır" },
      { icon: "ri-cursor-line",             color: "#34D399", title: "Kolay Kullanım",           description: "Tek el hareketiyle onlarca projeyi yeniden düzenleyin" },
    ],
    steps: [
      { icon: "ri-drag-drop-line",     label: "Tut" },
      { icon: "ri-arrow-up-down-line", label: "Sürükle" },
      { icon: "ri-drag-drop-line",     label: "Bırak" },
      { icon: "ri-save-line",          label: "Kaydedildi!" },
    ],
    ctaText: "Aktifleştirmek için geliştiriciniz ile iletişime geçin",
  },

  status_tags: {
    label: "Proje Durum Etiketleri",
    icon: "ri-price-tag-3-line",
    badgeText: "Premium Özellik",
    title: "Özel Durum Etiketleri",
    subtitle: "Projeleri görsel olarak kategorileyin",
    description: "Her proje için özel durum etiketleri oluşturun ve yönetin. Renk, isim ve grup sayısı üzerinde tam kontrol. Tamamlandı, Devam Ediyor gibi sabit etiketlerle sınırlı kalmayın.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20project%20status%20tags%20badges%20interface%20colorful%20labels%20management%20dashboard%20professional%20dark%20background%20glowing%20indicators%20cinematic&width=900&height=360&seq=st-hero-001&orientation=landscape",
    stats: [
      { value: "Özel", label: "Renk", color: "#EC4899" },
      { value: "Sınırsız", label: "Etiket", color: "#10B981" },
      { value: "Canlı", label: "Güncelleme", color: "#F59E0B" },
    ],
    advantages: [
      { icon: "ri-price-tag-3-line",       color: "#EC4899", title: "Özel Etiket Oluştur",      description: "Kendi durum etiketlerinizi adlandırın ve renklendirin" },
      { icon: "ri-palette-line",           color: "#10B981", title: "Renk Özelleştirme",       description: "Her etiket için preset veya özel HEX renk seçin" },
      { icon: "ri-edit-line",              color: "#60A5FA", title: "İsim Düzenleme",          description: "Mevcut etiketlerin adını ve rengini istediğiniz zaman değiştirin" },
      { icon: "ri-delete-bin-line",        color: "#F43F5E", title: "Etiket Silme",            description: "Kullanmadığınız etiketleri listeden kaldırın" },
      { icon: "ri-refresh-line",           color: "#A78BFA", title: "Otomatik Güncelleme",     description: "Etiket değişikliği tüm projelere anında yansır" },
      { icon: "ri-layout-grid-line",       color: "#34D399", title: "Proje Kartlarında",       description: "Belirlediğiniz etiketler proje kartında renkli badge olarak görünür" },
    ],
    steps: [
      { icon: "ri-price-tag-3-line", label: "Etiket Oluştur" },
      { icon: "ri-palette-line",     label: "Renk Seç" },
      { icon: "ri-save-line",        label: "Kaydet" },
      { icon: "ri-building-2-line",  label: "Projeye Ata" },
    ],
    ctaText: "Aktifleştirmek için geliştiriciniz ile iletişime geçin",
  },

  site_inspector: {
    label: "Görsel İnspektör",
    icon: "ri-cursor-line",
    badgeText: "Premium Özellik",
    title: "Görsel İnspektör",
    subtitle: "Sitenizi canlı olarak fare ile düzenleyin",
    description: "Herhangi bir sayfanın üzerine hover ederek anında içerik değiştirebilir, bölümleri canlı editörle yönetebilir, proje ve ortak kartlarını sayfadan ayrılmadan güncelleyebilirsiniz.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20web%20development%20workspace%20with%20floating%20UI%20panels%20and%20glowing%20code%20editor%20overlay%20on%20a%20website%20neon%20green%20and%20blue%20highlights%20professional%20dashboard%20interface%20dark%20background%20subtle%20grid%20pattern%20cinematic%20lighting%20ultra%20high%20quality%20digital%20art&width=900&height=360&seq=insp-hero-001&orientation=landscape",
    stats: [
      { value: "50+", label: "Düzenlenebilir Alan", color: "#10B981" },
      { value: "4", label: "Sayfa Desteği", color: "#60A5FA" },
      { value: "0", label: "Kod Gerektirmez", color: "#F59E0B" },
    ],
    advantages: [
      { icon: "ri-cursor-line",        color: "#10B981", title: "Hover ile Canlı Düzenleme",  description: "Her bölüme hover et, renkli çerçeve ve düzenleme araçları anında belirir" },
      { icon: "ri-pencil-fill",         color: "#60A5FA", title: "50+ Inline Metin Alanı",    description: "Başlıklar, açıklamalar, butonlar, istatistikler — tıkla ve düzenle" },
      { icon: "ri-building-2-line",     color: "#F59E0B", title: "Proje Kartı Editörü",       description: "12 alan: isim, konum, yıl, özellikler, YouTube ve harita linki" },
      { icon: "ri-arrow-go-back-line",  color: "#A78BFA", title: "Sınırsız Geri Alma",       description: "Ctrl+Z ile kaydetme noktasına dön, hiçbir değişiklikten korkma" },
      { icon: "ri-links-line",          color: "#F43F5E", title: "Ortak Yönetimi",            description: "11 alan: ortak adı, sektör, açıklama, etiket, proje sayısı" },
      { icon: "ri-layout-grid-line",    color: "#34D399", title: "4 Sayfa Desteği",           description: "Ana, Hakkımızda, Projeler ve Ortaklar sayfaları tek araçta" },
    ],
    steps: [
      { icon: "ri-cursor-line",      label: "Hover Yap" },
      { icon: "ri-pencil-fill",      label: "Düzenle" },
      { icon: "ri-save-line",        label: "Kaydet" },
      { icon: "ri-check-double-line", label: "Yayınla" },
    ],
    ctaText: "Aktifleştirmek için geliştirici ile iletişime geçin",
  },

  project_archive: {
    label: "Proje Arşivleme",
    icon: "ri-archive-line",
    badgeText: "Premium Özellik",
    title: "Proje Arşivleme",
    subtitle: "Projeleri siteden gizleyin, silmeden saklayın",
    description: "Eski veya tamamlanmış projeleri silmek zorunda kalmadan arşivleyin. Arşivlenen projeler sitede görünmez ama veriler korunur, istediğiniz zaman geri getirebilirsiniz.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20archive%20storage%20management%20interface%20boxes%20shelves%20glowing%20amber%20indicators%20professional%20project%20management%20dark%20background%20cinematic%20high%20quality&width=900&height=360&seq=ar-hero-001&orientation=landscape",
    stats: [
      { value: "Gizle", label: "Siteden", color: "#F59E0B" },
      { value: "Koru", label: "Verileri", color: "#10B981" },
      { value: "Geri", label: "Getir", color: "#60A5FA" },
    ],
    advantages: [
      { icon: "ri-archive-line",           color: "#F59E0B", title: "Güvenli Arşivleme",        description: "Proje verisi ve görselleri arşivde tam olarak korunur" },
      { icon: "ri-eye-off-line",           color: "#F43F5E", title: "Siteden Gizleme",          description: "Arşivlenen projeler site ziyaretçilerine gösterilmez" },
      { icon: "ri-inbox-unarchive-line",   color: "#10B981", title: "Kolayca Geri Getir",      description: "Arşivlenmiş proje tek tıkla tekrar yayına alınabilir" },
      { icon: "ri-folder-open-line",       color: "#A78BFA", title: "Arşiv Listesi",            description: "Tüm arşivlenmiş projeler ayrı sekmede görüntülenir" },
      { icon: "ri-delete-bin-line",        color: "#60A5FA", title: "Kalıcı Silme Alternatifi",  description: "Proje silmek yerine arşivleyin — geri dönüş imkanı her zaman var" },
      { icon: "ri-building-2-line",        color: "#34D399", title: "Panel Yönetimi",          description: "Aktif ve arşivlenen projeler ayrı sekmede düzenli görünür" },
    ],
    steps: [
      { icon: "ri-building-2-line",     label: "Proje Seç" },
      { icon: "ri-archive-line",         label: "Arşivle" },
      { icon: "ri-eye-off-line",         label: "Siteden Kaybolur" },
      { icon: "ri-inbox-unarchive-line", label: "Geri Getir" },
    ],
    ctaText: "Aktifleştirmek için geliştiriciniz ile iletişime geçin",
  },
};

// ── Editable Popup Config Storage (per-feature) ──────────────────────

export function getEditablePopupConfig(featureId: string): PremiumPopupConfig {
  try {
    const raw = localStorage.getItem(`yg_popup_cfg_${featureId}`);
    if (raw) {
      const overrides = JSON.parse(raw) as Partial<PremiumPopupConfig>;
      const base = PREMIUM_POPUP_CONFIGS[featureId];
      if (!base) return overrides as PremiumPopupConfig;
      return { ...base, ...overrides, advantages: overrides.advantages ?? base.advantages, stats: overrides.stats ?? base.stats, steps: overrides.steps ?? base.steps };
    }
  } catch { /**/ }
  return PREMIUM_POPUP_CONFIGS[featureId];
}

export function saveEditablePopupConfig(featureId: string, config: PremiumPopupConfig): void {
  localStorage.setItem(`yg_popup_cfg_${featureId}`, JSON.stringify(config));
}

// ── Generic Premium Locked Popup ──────────────────────────────────────────────

export interface PremiumPopupProps {
  config: PremiumPopupConfig;
  visible: boolean;
  position?: "right" | "right-bottom";
  inline?: boolean;
}

export function PremiumPopup({ config, visible, position = "right", inline = false }: PremiumPopupProps) {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (visible) setImgError(false);
  }, [visible]);

  const positionStyle: React.CSSProperties =
    position === "right-bottom"
      ? { width: "480px", left: "270px", bottom: "20px", top: "auto" }
      : { width: "480px", left: "270px", top: "auto", bottom: "20px" };

  const wrapperClass = inline
    ? `transition-all duration-300 w-full max-w-[480px] mx-auto ${visible ? "opacity-100" : "opacity-0 hidden"}`
    : `fixed z-[99999] transition-all duration-300 pointer-events-none ${visible ? "opacity-100 translate-x-0 pointer-events-auto" : "opacity-0 -translate-x-2 hidden"}`;

  const wrapperStyle = inline ? {} : positionStyle;

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      {!inline && (
        <div
          className="absolute left-0 bottom-8 -translate-x-full border-8 border-transparent"
          style={{ borderRightColor: "#0F1729" }}
        />
      )}

      <div
        className="bg-[#0A1020] border border-[#2B3654] rounded-2xl overflow-hidden"
        style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(84,102,142,0.2)" }}
      >
        {/* ── Hero ── */}
        <div className="relative h-44 overflow-hidden flex-shrink-0">
          {!imgError ? (
            <img
              src={config.heroImageUrl}
              alt={config.title}
              className="w-full h-full object-cover object-top"
              onError={() => setImgError(true)}
            />
          ) : (
            <div
              className="w-full h-full"
              style={{
                backgroundImage: "linear-gradient(rgba(84,102,142,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(84,102,142,0.1) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
                backgroundColor: "#0A1020",
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-[#0A1020]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1020]/30 to-transparent" />

          <div className="absolute top-3 left-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 backdrop-blur-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-vip-crown-fill text-amber-400 text-xs" />
              </div>
              <span className="text-amber-300 text-xs font-semibold">{config.badgeText}</span>
            </div>
          </div>

          <div className="absolute top-3 right-4 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm border border-white/10 rounded-full px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            <span className="text-white/70 text-[10px] font-medium">Kilitli</span>
          </div>

          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-white font-black text-xl leading-tight">{config.title}</h3>
            <p className="text-[#A5B5CC] text-sm mt-0.5">{config.subtitle}</p>
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-3 border-b border-[#1E2B4A]">
          {config.stats.map((s, i) => (
            <div key={i} className={`py-3 text-center ${i < 2 ? "border-r border-[#1E2B4A]" : ""}`}>
              <p className="font-black text-lg" style={{ color: s.color }}>{s.value}</p>
              <p className="text-[#5E7398] text-[10px] mt-0.5 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>

        {/* ── Description ── */}
        <div className="px-4 pt-4 pb-2">
          <p className="text-[#8BA0C0] text-xs leading-relaxed">{config.description}</p>
        </div>

        {/* ── Advantages ── */}
        <div className="px-4 pb-4 grid grid-cols-2 gap-2 mt-1">
          {config.advantages.map((adv, i) => (
            <div
              key={i}
              className="flex items-start gap-2.5 p-2.5 rounded-xl bg-[#141E35] border border-[#2B3654] hover:border-[#3a4d6e] transition-colors"
            >
              <div
                className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ background: adv.color + "22" }}
              >
                <i className={`${adv.icon} text-sm`} style={{ color: adv.color }} />
              </div>
              <div className="min-w-0">
                <p className="text-white text-[11px] font-semibold leading-tight">{adv.title}</p>
                <p className="text-[#5E7398] text-[10px] leading-relaxed mt-0.5">{adv.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Steps ── */}
        <div className="mx-4 mb-3 bg-[#0F1729] border border-[#2B3654] rounded-xl p-3">
          <p className="text-[#5E7398] text-[9px] uppercase tracking-widest font-semibold mb-2">Nasıl Çalışır?</p>
          <div className="flex items-center gap-1">
            {config.steps.map((step, i) => (
              <div key={i} className="flex items-center gap-1 flex-1">
                <div className="flex flex-col items-center gap-1 flex-1">
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#141E35] border border-[#2B3654]">
                    <i className={`${step.icon} text-[#7B8FAF] text-xs`} />
                  </div>
                  <p className="text-[#465886] text-[9px] text-center whitespace-nowrap">{step.label}</p>
                </div>
                {i < config.steps.length - 1 && (
                  <div className="flex-shrink-0 mb-3">
                    <i className="ri-arrow-right-line text-[#2B3654] text-xs" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="mx-4 mb-4 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-500/20 flex-shrink-0">
            <i className="ri-lock-fill text-amber-400 text-sm" />
          </div>
          <p className="text-amber-300/90 text-[11px] leading-relaxed flex-1">{config.ctaText}</p>
        </div>
      </div>
    </div>
  );
}

// ── Locked Sidebar Button w/ Rich Popup ──────────────────────────────────────

interface PremiumLockedButtonProps {
  featureId: string;
  position?: "right" | "right-bottom";
}

export default function PremiumLockedButton({ featureId, position = "right-bottom" }: PremiumLockedButtonProps) {
  const [showPopup, setShowPopup] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const config = getEditablePopupConfig(featureId);

  if (!config) return null;

  const handleMouseEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowPopup(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => setShowPopup(false), 150);
  };

  return (
    <div
      className="relative w-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium border border-amber-500/15 bg-amber-500/5 cursor-not-allowed select-none">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className={`${config.icon} text-base text-amber-400/70`} />
        </div>
        <span className="flex-1 truncate text-amber-300/70 text-sm">{config.label}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-lock-fill text-amber-500 text-xs" />
          </div>
          <span className="text-amber-500/60 text-[9px] font-semibold uppercase tracking-wide">Premium</span>
        </div>
      </div>
      <PremiumPopup config={config} visible={showPopup} position={position} />
    </div>
  );
}
